/**
 * Canonical identity provider names, mirroring `IdentityProviderName` in
 * `Quartz.Services.Core.Party.Domain/Identity/IdentityConstants.cs`.
 *
 * Keep the two in step. The membership API validates an incoming provider against its own
 * copy of this list and stores it in this casing, so anything reaching the UI should already
 * be one of these. The helpers below still normalise, because records written before that
 * validation existed are not guaranteed to.
 */
export const IdentityProvider = {
  Zing: 'Zing',
  Auth0: 'Auth0',
} as const;

const knownProviders: readonly string[] = Object.values(IdentityProvider);

/**
 * Resolves the provider value the API returned into something displayable (JXP-1479).
 *
 * Absent or empty means the person has never migrated, which is Zing. A recognised name comes
 * back in canonical casing so the UI never shows "auth0". An unrecognised name is returned
 * as-is rather than coerced to Zing: showing an unexpected provider is a far smaller problem
 * than reporting a migrated person as still being on the legacy provider.
 */
export function resolveIdentityProvider(provider?: string | null): string {
  const trimmed = (provider ?? '').trim();

  if (!trimmed) {
    return IdentityProvider.Zing;
  }

  return knownProviders.find(known => known.toLowerCase() === trimmed.toLowerCase()) ?? trimmed;
}

/**
 * True when credential actions that target the legacy Zing identity server would not affect
 * how this person actually signs in.
 *
 * Deliberately "is not Zing" rather than "is Auth0". If an unrecognised provider ever reaches
 * the UI, the safe default is to warn the representative rather than stay silent, since the
 * cost of a missing warning is a support rep resetting a credential that does nothing.
 */
export function isLegacyIdentityProviderMismatch(provider?: string | null): boolean {
  return resolveIdentityProvider(provider).toLowerCase() !== IdentityProvider.Zing.toLowerCase();
}

/**
 * Values shown in place of a provider name when no provider was read (JXP-1907).
 *
 * Identity > Users is served by the JM Identity store, which carries no provider of its own, so
 * the provider has to come from the membership person created off the identity user id. That
 * read can legitimately find nothing, and it can fail. Neither case may display as "Zing": a
 * silent legacy provider is what lets a representative send a temporary password that the
 * account the user actually signs in with never sees.
 */
export const IdentityProviderDisplay = {
  NoLinkedPerson: 'No linked person',
  Unavailable: 'Unavailable',
} as const;

/**
 * Resolves the provider of the membership person an identity user was looked up from
 * (JXP-1907).
 *
 * A missing person is an explicit state rather than a Zing default. Anything else defers to
 * resolveIdentityProvider so the trim and casing rules stay in one place.
 */
export function resolveLinkedPersonIdentityProvider(person?: { currentIdentityProvider?: string } | null): string {
  if (!person) {
    return IdentityProviderDisplay.NoLinkedPerson;
  }

  return resolveIdentityProvider(person.currentIdentityProvider);
}
